import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createPdiuProgram,
  getPdiuProgram,
} from "@/api/program-steps";

/**
 * POST /pdiu/program/{program_id}
 * Create a PDIU program
 */
export const useCreatePdiuProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, payload }: { programId: string; payload: object }) =>
      createPdiuProgram(programId, payload),
    onSuccess: (_, { programId }) => {
      // Invalidate the related PDIU query so that UI refreshes
      queryClient.invalidateQueries({ queryKey: ["pdiu-program", programId] });
    },
  });
};

/**
 * GET /pdiu/program/{program_id}
 * Get PDIU program data
 */
export function useGetPdiuProgram(programId: string) {
  return useQuery({
    queryKey: ["pdiu-program", programId],
    queryFn: async () => {
      try {
        return await getPdiuProgram(programId);
      } catch (error: any) {
        if (error?.status === 404) {
          return { data: null, status: "success", message: "No PDIU found" };
        }
        // For other errors, rethrow
        throw error;
      }
    },
    enabled: !!programId,
    retry: false, // Don't retry if the PDIU doesn't exist
    refetchOnWindowFocus: false, // Don't refetch on window focus
    staleTime: Infinity, // Once we get a response, don't refetch
  });
}